import { useCallback, useMemo, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { toast } from 'sonner'
import type { OrganizationUsersSearchParams } from '@/features/organization'
import {
  InviteOrganizationUserButton,
  OrganizationUsersTable,
} from '@/features/organization'
import { useOrganizationUsers } from '@/features/organization/services/organization.convex'
import {
  AuthGuard,
  useCurrentOrganizationId,
  useMyConvexOrganizationId,
  usePermissions,
} from '@/features/auth'

export const Route = createFileRoute('/(dashboard)/organization/overview/')({
  component: RouteComponent,
  beforeLoad: () => ({
    title: 'Organization Overview',
    breadcrumbs: [{ title: 'Organization' }, { title: 'Overview' }],
  }),
})

function RouteComponent() {
  const organizationId = useCurrentOrganizationId()
  const convexOrganizationId = useMyConvexOrganizationId()
  const { hasOrganizationRole } = usePermissions()
  const isPrivilegedView =
    !!organizationId &&
    (hasOrganizationRole(organizationId, 'admin') ||
      hasOrganizationRole(organizationId, 'manager'))
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 10

  const [filters, setFilters] = useState<OrganizationUsersSearchParams>({})

  const { data: allUsers, isLoading } =
    useOrganizationUsers(convexOrganizationId)

  // Client-side filtering — Convex already returns all users reactively
  const filteredUsers = useMemo(() => {
    return allUsers.filter((u) => {
      if (filters.search) {
        const term = filters.search.toLowerCase()
        if (
          !u.name?.toLowerCase().includes(term) &&
          !u.email?.toLowerCase().includes(term)
        ) {
          return false
        }
      }
      if (
        filters.userIds &&
        filters.userIds.length > 0 &&
        !filters.userIds.includes(u.id)
      )
        return false
      if (filters.roles && filters.roles.length > 0) {
        const pos = u.userPosition?.positionTrajectory.fromPosition
        if (!pos?.role || !filters.roles.includes(String(pos.role)))
          return false
      }
      if (filters.positionLevels && filters.positionLevels.length > 0) {
        const pos = u.userPosition?.positionTrajectory.fromPosition
        if (!pos?.level || !filters.positionLevels.includes(String(pos.level)))
          return false
      }
      if (filters.orgRoleIds && filters.orgRoleIds.length > 0) {
        const userRoleIds = u.roles.map((r) => r.id)
        if (!filters.orgRoleIds.some((id) => userRoleIds.includes(id)))
          return false
      }
      return true
    })
  }, [allUsers, filters])

  const totalUsers = filteredUsers.length
  const totalPages = Math.max(1, Math.ceil(totalUsers / pageSize))
  const pagedUsers = filteredUsers.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  )

  const navigate = useNavigate()

  const handleFilterChange = useCallback(
    (newFilters: OrganizationUsersSearchParams) => {
      setFilters(newFilters)
      setCurrentPage(1)
    },
    [],
  )

  return (
    <AuthGuard
      requiredOrganizationRole={{
        organizationId: organizationId || '',
        role: ['admin', 'manager', 'member', 'analytics', 'billing'],
      }}
    >
      <div className="space-y-6">
        {/* Header Section */}
        <div className="flex items-center justify-between p-6 bg-muted/30 rounded-lg">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">
              Organization Users
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {isPrivilegedView
                ? 'View and manage organization users'
                : 'Your colleagues in this organization'}
            </p>
          </div>
          {isPrivilegedView && (
            <InviteOrganizationUserButton
              onSuccess={() => {
                toast.success('Invitation sent successfully!')
                navigate({ to: '/organization/invites' })
              }}
            />
          )}
        </div>

        {/* Table Section */}
        <OrganizationUsersTable
          users={pagedUsers}
          isLoading={isLoading}
          currentPage={currentPage}
          totalPages={totalPages}
          totalUsers={totalUsers}
          onPageChange={setCurrentPage}
          filters={filters}
          onFilterChange={handleFilterChange}
        />
      </div>
    </AuthGuard>
  )
}
